import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { QUERY_KEYS } from '@/constants/query'
import { TIME } from '@/constants/time'
import useMessaging from '@/composables/tithelyMessaging'
import type { GetConversationsParams } from '@/types/composables/tithelyMessaging'

/** How many conversations the Inbox Lite panel shows. */
const INBOX_LITE_LIMIT = 10

// Newest reply first: the panel is a glanceable "who replied" list, so it sorts on
// when the contact last wrote back, not on the org's own outbound activity. Only
// conversations with an unread inbound message are requested, so the page limit
// isn't spent on threads that need no attention.
const INBOX_LITE_PARAMS: GetConversationsParams = {
    message_type: `sms`,
    unread: true,
    sort: `last_receive_date`,
    sort_order: `desc`,
    limit: INBOX_LITE_LIMIT
}

/**
 * The org's most recent SMS conversations for the Inbox Lite panel, from
 * `GET /v1/organizations/{org_id}/conversations`. Pass `enabled` so the list is
 * only fetched while the panel is open; `useMessagingConversationChannel` events
 * invalidate the key so an open panel refreshes live.
 */
const useInboxConversations = (enabled: Ref<boolean> | boolean = true) => {
    const { getConversations } = useMessaging()

    return useQuery({
        queryKey: QUERY_KEYS.inboxConversations(),
        queryFn: () => getConversations(INBOX_LITE_PARAMS),
        select: response => response.data,
        enabled: computed(() => unref(enabled)),
        staleTime: TIME.Second(30),
        retry: 1
    })
}

/**
 * The org's unread inbound message count for the Inbox Lite badge, from
 * `GET /v1/organizations/{org_id}/unread-messages/me`. Unlike the panel's list it
 * is fetched up front, since the badge is always visible. Cached as the bare count
 * so `unread-message-status-update` events, which carry the same org-wide number,
 * can replace it via `useSetUnreadMessageCount` instead of triggering a refetch.
 */
const useUnreadMessageCount = (enabled: Ref<boolean> | boolean = true) => {
    const { getUnreadMessageStats } = useMessaging()

    return useQuery({
        queryKey: QUERY_KEYS.unreadMessageCount(),
        queryFn: async () => (await getUnreadMessageStats()).data.unread_count,
        enabled: computed(() => unref(enabled)),
        staleTime: TIME.Second(30),
        retry: 1
    })
}

/** Writes a pushed unread count into the cache `useUnreadMessageCount` reads. */
const useSetUnreadMessageCount = () => {
    const queryClient = useQueryClient()

    return (count: number) => queryClient.setQueryData(QUERY_KEYS.unreadMessageCount(), count)
}

export { useInboxConversations, useUnreadMessageCount, useSetUnreadMessageCount, INBOX_LITE_LIMIT }
